// ABOUTME: Right-side drawer that renders the Configurator component for a prototype.
// ABOUTME: Used in the CPQ flow to configure a product before adding it as a line item.

import { Configurator, SidebarProvider, TUserAssignment } from '@coolshop/cool-configurator';
import {
    Box,
    Button,
    CircularProgress,
    Drawer,
    IconButton,
    MenuItem,
    SaveAsOutlinedIcon,
    Select,
    Snackbar,
    Tooltip,
} from '@ds/coolshop';
import { useConfiguratorCurrency } from '@src/_new/hooks/useConfiguratorCurrency';
import { ConfiguratorThemeProvider } from '@src/_new/modules/cpq/components/ConfiguratorTheme/ConfiguratorThemeProvider';
import { ConfiguratorProduct } from '@src/_new/utils/configuratorProduct';
import { getForceFinalizeEnabled } from '@src/_new/utils/getForceFinalizeEnabled';
import { getHideOptionCode } from '@src/_new/utils/getHideOptionCode';
import {
    useGetNodeQuery,
    useGetObjectSchemaDetailQuery,
} from '@src/components/hierarchies/Hierarchy.generated';
import useTransformEditNodeData from '@src/components/hierarchies/hooks/useTransformEditNodeData';
import { useAuth } from '@src/hooks';
import useFetchModel from '@src/modules/prototype/hooks/useFetchModel';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { ComponentProps, ReactNode, useContext, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useCadDownload } from '../../hooks/useCadDownload';
import { useConfiguratorDefaults } from '../../hooks/useConfiguratorDefaults';
import { useConfiguratorLayout } from '../../hooks/useConfiguratorLayout';
import {
    useIsAdditionalInfoEnabled,
    useIsAddLinkedProductEnabled,
    useIsAddTemporaryFeatureEnabled,
    useIsAddTemporaryWarrantyEnabled,
    useIsFindSimilarEnabled,
} from '../../hooks/useCpqSettings';
import { AceView } from '../../types';
import { getFeaturesHiddenPrice } from '../../utils/getFeaturesHiddenPrice';
import { isCadDownloadAvailable, TCadPreview } from '../../utils/isCadDownloadAvailable';
import { CPQ_PRICING_DEBUG_ROLE, PricingTraceDrawer } from '../PricingTrace';
import { CadDownloadMenu, CadDownloadSelection, TAttachment } from './CadDownloadMenu';
import { ConfiguratorFavoriteAction } from './ConfiguratorFavoriteAction';
import { SaveTemplateDialog } from './SaveTemplateDialog';

interface ConfiguratorDrawerProps {
    open: boolean;
    prototypeId: string;
    onClose: () => void;
    /** Never called in "view" mode, where the configurator cannot be finalized. */
    onFinalize?: (configurationJson: string, productId: string) => void;
    initialUserAssignments?: TUserAssignment[];
    /**
     * Configit view the configuration being reopened was made against, so a saved line is shown
     * (and re-resolved) under the same view. Omit it and the model's first view is preselected.
     */
    initialViewId?: string;
    mode?: 'add' | 'edit' | 'view';
    showSaveAsTemplate?: boolean;
    /**
     * Header title, overriding the one `mode` derives. `mode` drives two unrelated things — whether
     * the seller's configurator defaults are merged over the passed assignments, and the header
     * string — so a flow that needs "edit" semantics under its own title sets this instead of
     * introducing a mode.
     */
    title?: string;
    /**
     * Where the seller is in a multi-step flow, shown as the breadcrumb over the product name in
     * the configurator's sidebar. Labels only: the slot that renders them takes a string.
     */
    breadcrumbSteps?: string[];
    /** Index into {@link breadcrumbSteps} of the step being configured; the path stops there. */
    activeStepIndex?: number;
    /**
     * Rendered last in the right header cluster, at its right edge. Replaces the configurator
     * package's finalize button (which cannot be relabelled) with the flow's own primary actions.
     * `requestFinalize` hands the current selections to `onFinalize` straight away, without going
     * through the package's finalize, which refuses a disabled or incomplete configuration. Gating
     * is the caller's: `isComplete` mirrors the configurator package's own finalize button (which
     * this replaces), so the caller can gate its finalize on a fully resolved configuration.
     */
    headerActions?: (requestFinalize: () => void, isComplete: boolean) => ReactNode;
    /** Shows the favorite star for this prototype first in the right header cluster. */
    showFavoriteAction?: boolean;
    /**
     * The offer this configurator was opened from, and the line item being configured when one
     * exists. The server reads the offer from these and stamps its metadata on the CAD drawing, so a
     * generated datasheet can be traced back to its quote. Omitted outside an offer — a prototype
     * preview or a template has no quote, and a new line has no number yet.
     */
    offerId?: string;
    lineItemId?: string;
}

export function ConfiguratorDrawer({
    open,
    prototypeId,
    onClose,
    onFinalize,
    initialUserAssignments,
    initialViewId,
    mode = 'add',
    showSaveAsTemplate = true,
    title,
    breadcrumbSteps,
    activeStepIndex = 0,
    headerActions,
    showFavoriteAction = false,
    offerId,
    lineItemId,
}: ConfiguratorDrawerProps) {
    // Read-only inspection of a saved configuration: the configurator package disables every
    // selection through its ConfiguratorDisabledProvider, and both ways out of the drawer that
    // would persist a change — finalize and save-as-template — are hidden.
    const isView = mode === 'view';
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);
    // Saving a configuration as a template requires the OFFER_TEMPLATE hierarchy to be set up in
    // system settings; without it there is nowhere to persist the template, so hide the action.
    const isTemplatesHierarchyConfigured = Boolean(settings.cpq?.hierarchies?.templates);
    // Four of the configurator package's action-header buttons are opt-in: each stays hidden
    // unless its own CPQ setting is on.
    const isFindSimilarEnabled = useIsFindSimilarEnabled();
    const isAddLinkedProductEnabled = useIsAddLinkedProductEnabled();
    const isAddTemporaryFeatureEnabled = useIsAddTemporaryFeatureEnabled();
    const isAddTemporaryWarrantyEnabled = useIsAddTemporaryWarrantyEnabled();
    const isAdditionalInfoEnabled = useIsAdditionalInfoEnabled();
    const currency = useConfiguratorCurrency();
    // Drawer and panel widths, all tenant-configurable; every one of them has a default.
    const {
        drawerWidth,
        sidebarWidth,
        recapWidth,
        defaultGroupsExpanded,
        defaultRecapFeaturesExpanded,
        defaultRecapExpanded,
    } = useConfiguratorLayout();
    const transformEditNodeData = useTransformEditNodeData();
    const { defaults: configuratorDefaults, loading: loadingConfiguratorDefaults } =
        useConfiguratorDefaults();
    const [templateConfigJson, setTemplateConfigJson] = useState<string>();
    const [explainConfigJson, setExplainConfigJson] = useState<string>();
    const { hasUserRole } = useAuth();

    // Feature codes whose price the configurator must hide. Derived from the product returned by
    // the be-engine apply-assignments responses (the server flags it with hidePrices when the
    // model opts in), since the drawer no longer fetches the product itself.
    const [featuresHiddenPrice, setFeaturesHiddenPrice] = useState<string[]>([]);

    // The same hidePrices flag also hides the header's Total Price: with every feature price
    // hidden, the total is meaningless.
    const [hideTotalPrice, setHideTotalPrice] = useState(false);

    // Same source: the server flags the product with hideOptionCode when the model opts in.
    const [hideOptionCode, setHideOptionCode] = useState(false);

    // Same source again: the model opted into finalizing an incomplete configuration, so the
    // configurator keeps its finalize button enabled while still reporting the engine's status.
    const [forceFinalizeEnabled, setForceFinalizeEnabled] = useState(false);

    // Whether every required feature is resolved, as the configurator package itself judges it:
    // the very verdict its own finalize button applies, so the flow's finalize buttons
    // (headerActions) can never disagree with it. Reported false again when it unmounts.
    const [configurationComplete, setConfigurationComplete] = useState(false);

    // The model's own documents, taken off the product before the package sees it. The package
    // renders an info button of its own in the sidebar for whatever `attachments` the product
    // carries; inside the drawer they belong to the header's download menu instead, so they are
    // moved here rather than shown twice.
    const [modelAttachments, setModelAttachments] = useState<TAttachment[]>([]);

    // The server's CAD verdict for the configuration as it stands, recomputed on every
    // interaction and carried on the same responses. It gates the download menu instead of
    // completeness, which is only one of the things it accounts for. Undefined until the first
    // response, and whenever the tenant or the model has no CAD at all.
    const [cadPreview, setCadPreview] = useState<TCadPreview | undefined>(undefined);

    // The CADENAS artifacts this tenant offers for this product, plus the download itself. The
    // options are fetched once per product; a tenant without the download block gets none, and the
    // menu then renders nothing at all. Progress and failures are narrated by the shared toasts the
    // hook raises, so there is nothing to surface from here.
    const {
        options: cadDownloadOptions,
        downloadArtifact,
        busyEntry: cadDownloadBusyEntry,
    } = useCadDownload(prototypeId, !open);

    // The views the prototype's model exposes, as reported by the apply-assignments responses.
    // Empty for a model with no view and for the coolshop engine, in which case no picker is
    // rendered and no viewId is ever sent.
    const [views, setViews] = useState<AceView[]>([]);
    // Set only by the picker: until the seller chooses, the reopened line's view wins, then the
    // model's first view — which is also the default the server applies to a request without one.
    const [selectedViewId, setSelectedViewId] = useState<string | undefined>();
    const effectiveViewId = selectedViewId ?? initialViewId ?? views[0]?.id;
    // Read by the http client, which is memoized with an empty dep array on purpose (the
    // configurator package rebuilds its engine when the client identity changes). Assigned during
    // render rather than in an effect: child effects run before the parent's, so an effect would
    // leave the configurator's very first request without the view.
    const viewIdRef = useRef<string | undefined>(effectiveViewId);
    viewIdRef.current = effectiveViewId;
    // Read by the same http client and assigned during render for the same reason: the offer this
    // configuration belongs to resolves asynchronously, and an effect would leave the first request —
    // the one that builds the baseline CAD preview — without the quote to stamp on the drawing.
    const offerRef = useRef<{ offerId?: string; lineItemId?: string }>({ offerId, lineItemId });
    offerRef.current = { offerId, lineItemId };
    // Selections of the last configurator request: the current configuration. Recorded so a view
    // switch can re-seed them and so the drawer's own buttons (headerActions, "Save as Template",
    // "Explain price") can hand the configuration on without going through the package's finalize.
    const lastAssignmentsRef = useRef<TUserAssignment[]>([]);
    // Selections carried into the view the seller just switched to. Its own state because the
    // userAssignments memo below derives from the prop, which does not survive the remount.
    const [switchedAssignments, setSwitchedAssignments] = useState<TUserAssignment[]>();
    // Armed by a view switch, disarmed by the first response after it: see the retry in the client.
    const retryWithoutAssignmentsRef = useRef(false);
    const [viewSwitchResetNotice, setViewSwitchResetNotice] = useState(false);

    // In "add" mode pre-apply the user's saved defaults, letting any explicitly passed
    // initialUserAssignments win per featureCode. In "edit" mode the saved selections are
    // authoritative, so defaults are never injected.
    const userAssignments = useMemo<TUserAssignment[] | undefined>(() => {
        // After a view switch the carried-over selections are authoritative: the seller made them,
        // and any default was already applied to the configuration they were captured from.
        if (switchedAssignments) return switchedAssignments;
        if (mode !== 'add') return initialUserAssignments;
        const overriddenFeatures = new Set(
            (initialUserAssignments ?? []).map(assignment => assignment.featureCode),
        );
        const defaultAssignments: TUserAssignment[] = configuratorDefaults
            .filter(item => !overriddenFeatures.has(item.featureCode))
            .map(item => ({
                featureCode: item.featureCode,
                value: { optionCode: item.optionCode, isSelected: true },
            }));
        const merged = [...defaultAssignments, ...(initialUserAssignments ?? [])];
        return merged.length > 0 ? merged : undefined;
    }, [mode, configuratorDefaults, initialUserAssignments, switchedAssignments]);

    const { data: prototypeNodeData, loading: loadingPrototypeNode } = useGetNodeQuery({
        variables: {
            hierarchiesId: settings.prototype?.hierarchies?.prototype,
            nodeFilter: { ids: [prototypeId] },
        },
        fetchPolicy: 'network-only',
        skip: !open || !prototypeId,
    });

    const prototypeNode = useMemo(
        () => transformEditNodeData(prototypeNodeData ?? {}),
        [prototypeNodeData, transformEditNodeData],
    );

    const { data: prototypeSchemaData, loading: loadingPrototypeSchema } =
        useGetObjectSchemaDetailQuery({
            variables: { hierarchiesId: settings.prototype?.hierarchies?.prototype },
            skip: !open || !settings.prototype?.hierarchies?.prototype,
        });

    const modelContentTypeCode = useMemo(() => {
        const modelHierarchyId = settings.prototype?.hierarchies?.model;
        if (!modelHierarchyId) return undefined;
        return prototypeSchemaData?.hierarchies?.[0]?.objectSchema?.contentTypeGroups
            ?.flatMap(group => group.contentTypes ?? [])
            .find(
                contentType =>
                    contentType.type === 'link' && contentType.hierarchy === modelHierarchyId,
            )?.code;
    }, [prototypeSchemaData, settings.prototype?.hierarchies?.model]);

    const modelId = useMemo(() => {
        if (!modelContentTypeCode) return undefined;
        const raw = prototypeNode?.contents?.find(c => c.contentType === modelContentTypeCode)
            ?.value?.[0]?.value?.[0];
        return typeof raw === 'string' ? raw : undefined;
    }, [prototypeNode, modelContentTypeCode]);

    const { loadingModelNode } = useFetchModel({ cgtModelId: modelId ?? '' });

    // The backend decides coolshop vs configit from the model's configuratorType; the drawer always
    // uses the be-engine client so the frontend never runs the engine locally. baseUrl '/api' so the
    // engine posts to /api/configurator/apply-assignments, which the dev proxy forwards to the backend.
    const configuratorConfig = useMemo<ComponentProps<typeof Configurator>['config']>(
        () => ({ type: 'be-engine', baseUrl: '/api' }),
        [],
    );

    // Real HTTP client for the be-engine path: each option selection posts to the backend engine
    // (via the config baseUrl), unlike the coolshop/configit paths which use a mocked client. A
    // minimal fetch-based client is enough — the engine only calls post(url, body) and reads data.
    const beEngineHttpClient = useMemo(
        () =>
            ({
                post: async (url: string, body: unknown) => {
                    // Both configurator endpoints: /apply-assignments and its /state sibling.
                    const isConfiguratorRequest = url.includes('/configurator/apply-assignments');
                    // The configurator package builds every request body from a fixed field list and
                    // knows neither about views nor about the offer it is being opened from, so the
                    // drawer merges both in here: the single seam every configurator request passes
                    // through. Each is left out when it has nothing to say, so a request carries only
                    // what applies to it.
                    const requestBody = isConfiguratorRequest
                        ? {
                              ...(body as Record<string, unknown>),
                              ...(viewIdRef.current ? { viewId: viewIdRef.current } : {}),
                              ...(offerRef.current.offerId
                                  ? {
                                        offerId: offerRef.current.offerId,
                                        ...(offerRef.current.lineItemId
                                            ? { lineItemId: offerRef.current.lineItemId }
                                            : {}),
                                    }
                                  : {}),
                          }
                        : body;
                    // Passively record the selections so a mid-flow view switch can re-seed them.
                    // A finalize cannot serve that: it runs the package's completeness gate and may
                    // emit nothing from the half-finished configuration a switch works from.
                    if (isConfiguratorRequest) {
                        const sent = (body as { userAssignments?: TUserAssignment[] })
                            ?.userAssignments;
                        if (Array.isArray(sent)) lastAssignmentsRef.current = sent;
                    }

                    const send = (payload: unknown) =>
                        fetch(url, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                        });

                    let res = await send(requestBody);
                    // First request after a view switch: the carried-over selections may not exist
                    // in the new view. Retry once from a clean slate rather than leaving the seller
                    // in front of a configurator that cannot resolve.
                    if (isConfiguratorRequest && !res.ok && retryWithoutAssignmentsRef.current) {
                        retryWithoutAssignmentsRef.current = false;
                        lastAssignmentsRef.current = [];
                        setSwitchedAssignments([]);
                        setViewSwitchResetNotice(true);
                        res = await send({
                            ...(requestBody as Record<string, unknown>),
                            userAssignments: [],
                        });
                    } else if (isConfiguratorRequest && res.ok) {
                        retryWithoutAssignmentsRef.current = false;
                    }
                    const json = await res.json();
                    // Apply-assignments responses carry the resolved product; assignment-state
                    // responses don't, so the product-derived presentational flags are only
                    // refreshed when the product is present.
                    const data = (
                        json as {
                            data?: {
                                product?: ConfiguratorProduct;
                                views?: AceView[];
                                cadPreview?: TCadPreview;
                            };
                        }
                    )?.data;
                    const product = data?.product;
                    if (product) {
                        setFeaturesHiddenPrice(getFeaturesHiddenPrice(product));
                        setHideTotalPrice(Boolean(product.hidePrices));
                        setHideOptionCode(getHideOptionCode(product));
                        // Moved, not copied: the package shows a sidebar info button for every
                        // product carrying attachments, and the drawer offers its documents from
                        // the header menu alone. Deleting the field is the only way to silence
                        // that button — the package exposes no prop for it.
                        setModelAttachments((product.attachments as TAttachment[]) ?? []);
                        delete product.attachments;
                        setForceFinalizeEnabled(getForceFinalizeEnabled(product));
                    }
                    // Sibling of `product`, not a field of it: the service attaches it after the
                    // provider answered, and it rides on the state responses too, so it is
                    // refreshed on every interaction. Absent means no CAD for this tenant/model.
                    if (isConfiguratorRequest) setCadPreview(data?.cadPreview);
                    // The views travel with the apply-assignments responses only, so the guard
                    // keeps a later product-less response from clearing the picker.
                    if (data?.views) setViews(data.views);
                    return { data: json };
                },
            } as unknown as Parameters<typeof Configurator>['0']['httpClientInstance']),
        [],
    );

    // Every payload carries the view, so explaining a price, applying a template and
    // re-pricing a saved line all resolve under the view the seller configured against.
    // JSON.stringify drops it when there is none, leaving today's envelope untouched. The productId
    // is the prototype, the same one every configurator request sends and the one the server
    // resolves the model from; the package's finalize reports the product's internalId instead,
    // which apply-assignments never sets.
    const buildConfigurationJson = (assignments: unknown[]) =>
        JSON.stringify({
            userAssignments: assignments,
            productId: prototypeId,
            prototypeId,
            viewId: effectiveViewId,
        });

    // Reached only through the package's own finalize button, rendered when the caller supplies no
    // headerActions.
    const handleFinalize = (finalizedAssignments: unknown[]) =>
        onFinalize?.(buildConfigurationJson(finalizedAssignments), prototypeId);

    // The drawer's own buttons never go through the package's finalize: it refuses a disabled
    // configuration (view mode, where re-explaining a saved line is a primary use case) and an
    // incomplete one, and whether a button needs a complete configuration is the button's call, not
    // the package's. They read the recorded current configuration instead.
    const currentConfigurationJson = () => buildConfigurationJson(lastAssignmentsRef.current);

    const handleSaveAsTemplate = () => setTemplateConfigJson(currentConfigurationJson());

    const handleExplainPrice = () => setExplainConfigJson(currentConfigurationJson());

    // Deliberately *not* driven by a finalize, unlike "Save as Template" and "Explain price": the
    // package refuses to finalize an incomplete configuration, while a CAD download is allowed on
    // one precisely when the tenant set `allowIncompleteConfiguration` — the setting exists for
    // models that can never be completed for reasons unrelated to CAD. The assignments of the last
    // configurator request describe the configuration exactly as it stands, and the server
    // re-resolves them through the engine anyway, so nothing is trusted from here that a finalize
    // would have made safer.
    const handleCadDownload = (selection: CadDownloadSelection) => {
        const request = {
            configurationJson: JSON.stringify({
                userAssignments: lastAssignmentsRef.current,
                productId: prototypeId,
                prototypeId,
                viewId: effectiveViewId,
            }),
            format: selection.format,
            // Lets the server resolve quoteInfo itself from the offer, the same way /apply-assignments
            // does — the companion fix to accepting no client-supplied quoteInfo on this endpoint.
            offerId: offerRef.current.offerId,
            lineItemId: offerRef.current.lineItemId,
        };
        // Fire and forget: the hook owns the busy state and narrates the outcome through the
        // shared toasts.
        downloadArtifact(request).catch(() => {});
    };

    // Switching mid-flow: carry the current selections into the new view and let ACE reconcile
    // them, dropping whatever is not valid there. The configurator is remounted (its key carries
    // the view) so no state resolved under the previous view survives.
    const handleViewChange = (viewId: string) => {
        if (viewId === effectiveViewId) return;
        setSwitchedAssignments(lastAssignmentsRef.current);
        setSelectedViewId(viewId);
        retryWithoutAssignmentsRef.current = true;
    };

    // Handed to headerActions so a caller can finalize from its own button without reaching into
    // the drawer's refs.
    const requestFinalize = () => onFinalize?.(currentConfigurationJson(), prototypeId);

    // The drawer has a single header: the configurator package's own bar. Everything the drawer
    // contributes to it is built here and handed over as slots. The bar is an AppBar on the primary
    // colour, so every control carries the design system's contrast API.

    // `mode` derives the string when the caller passes none. Three literal formatMessage calls
    // rather than a lookup: the label extractor reads them statically and sees no id it would have
    // to resolve through a table.
    const modeTitle = () => {
        if (isView)
            return formatMessage({
                id: 'cpq.configurator.view-title',
                defaultMessage: 'Configuration Detail',
            });
        if (mode === 'edit')
            return formatMessage({
                id: 'cpq.configurator.edit-title',
                defaultMessage: 'Edit Configuration',
            });
        return formatMessage({ id: 'cpq.configurator.title', defaultMessage: 'Configure Product' });
    };
    const headerTitle = title ?? modeTitle();

    // The chain steps, then what this drawer is: the crumbs read from where the seller stands in
    // the flow down to the configuration in front of them. It goes into the sidebar, on the line
    // above the product name, rather than into the header: the sidebar already names the product,
    // and showing it in both places said the same thing twice.
    // Plain text, not JSX: the slot is PageHeader's `overtext`, which takes a string. The path
    // stops at the active step — that is what says where the seller stands, now that no crumb can
    // be emboldened.
    const sidebarSubtitle = [
        ...(breadcrumbSteps ?? []).slice(0, activeStepIndex + 1),
        headerTitle,
    ].join(' \u203a ');

    const saveAsTemplateLabel = formatMessage({
        id: 'cpq.configurator.save-as-template',
        defaultMessage: 'Save as Template',
    });

    const headerEndChildren = [
        ...(showFavoriteAction ? [<ConfiguratorFavoriteAction prototypeId={prototypeId} />] : []),
        <CadDownloadMenu
            options={cadDownloadOptions}
            attachments={modelAttachments}
            cadAvailable={isCadDownloadAvailable(cadPreview)}
            busyEntry={cadDownloadBusyEntry}
            onSelect={handleCadDownload}
        />,
        /* Only a model with an actual choice gets a picker: a single view is used silently, and no
           view at all sends nothing. Disabled in view mode, so a saved line shows which view priced
           it without allowing a switch. */
        ...(views.length > 1
            ? [
                  <Box minWidth="200px">
                      <Select
                          label={formatMessage({
                              id: 'cpq.configurator.view',
                              defaultMessage: 'View',
                          })}
                          value={effectiveViewId ?? ''}
                          onChange={e => handleViewChange(e.target.value as string)}
                          disabled={isView}
                          size="xs"
                          contrastColor="primary"
                          fullWidth
                      >
                          {views.map(view => (
                              <MenuItem key={view.id} value={view.id}>
                                  {view.label}
                              </MenuItem>
                          ))}
                      </Select>
                  </Box>,
              ]
            : []),
        // A round icon like the configurator's own header actions, the label in its tooltip: it
        // saves a template and leaves the configuration open, so it is not a primary action.
        ...(showSaveAsTemplate && !isView && isTemplatesHierarchyConfigured
            ? [
                  <Tooltip title={saveAsTemplateLabel} placement="bottom">
                      <IconButton
                          variant="contained"
                          size="medium"
                          invertedColor
                          contrastColor="primary"
                          aria-label={saveAsTemplateLabel}
                          onClick={handleSaveAsTemplate}
                      >
                          <SaveAsOutlinedIcon />
                      </IconButton>
                  </Tooltip>,
              ]
            : []),
        /* Also offered in view mode: re-explaining a saved line is a primary use case, and the
           explain reads the recorded configuration rather than finalizing. */
        ...(hasUserRole(CPQ_PRICING_DEBUG_ROLE)
            ? [
                  // "text" rather than "outlined": the DS Button explicitly disables the outlined
                  // variant, and text is its secondary-action style.
                  <Button
                      variant="text"
                      color="primary"
                      invertedColor
                      contrastColor="primary"
                      onClick={handleExplainPrice}
                  >
                      {formatMessage({
                          id: 'cpq.configurator.explain-price',
                          defaultMessage: 'Explain price',
                      })}
                  </Button>,
              ]
            : []),
        // Last: the flow's primary action closes the header at its right edge.
        ...(headerActions ? [<>{headerActions(requestFinalize, configurationComplete)}</>] : []),
    ];

    return (
        <Drawer anchor="right" width={drawerWidth} open={open} onClose={onClose}>
            {/* The configurator owns the whole drawer: its own ActionHeader is the only header bar
                and its chrome (bar, columns) sits flush against the drawer edges. `relative` so the
                prerequisite overlay below covers exactly it. */}
            <Box height="100vh" position="relative">
                {/* Mounted once the prototype prerequisites are ready; from there the configurator
                    package owns the loading/blocking (spinner + backdrop) during apply-assignments. */}
                {!loadingPrototypeNode &&
                    !loadingPrototypeSchema &&
                    !loadingModelNode &&
                    !loadingConfiguratorDefaults && (
                        <ConfiguratorThemeProvider>
                            <SidebarProvider
                                // Back closes the drawer: the configurator is the whole page here, so
                                // leaving it is the only thing "back" can mean. It is also the single
                                // way out, which is why the header passes no onClose and carries no
                                // close button of its own. The control lives in the sidebar header,
                                // and the bar takes it over, with the product name, once the sidebar
                                // is collapsed.
                                onBackButtonClick={onClose}
                            >
                                <Configurator
                                    // The view is part of the identity of a configuration: a
                                    // switch remounts, dropping the state the previous view
                                    // resolved. Keyed on the explicitly chosen view only — the
                                    // model's first view arrives with the first response, and
                                    // keying on it would remount the configurator the moment its
                                    // own answer landed, re-issuing the very same request.
                                    key={`${prototypeId}-${
                                        selectedViewId ?? initialViewId ?? 'default'
                                    }`}
                                    config={configuratorConfig}
                                    productId={prototypeId}
                                    featuresHiddenPrice={featuresHiddenPrice}
                                    hideOptionCode={hideOptionCode}
                                    forceFinalizeEnabled={forceFinalizeEnabled}
                                    currency={currency}
                                    // Unlike hideOptionCode/featuresHiddenPrice this is not derived from the
                                    // resolved product: it is an intentional host-level UI toggle with no
                                    // per-model/data-sensitivity meaning, so it is always enabled here.
                                    enableFeatureSearch
                                    showAdditionalInfo={isAdditionalInfoEnabled}
                                    onCanFinalizeChange={setConfigurationComplete}
                                    onFinalize={handleFinalize}
                                    userAssignments={userAssignments}
                                    disabled={isView}
                                    // The drawer has no bar of its own: every drawer action lives in
                                    // the configurator's own header, and what is being configured is
                                    // named once, by the sidebar.
                                    actionHeaderProps={{
                                        hideAddTemporaryFeature: !isAddTemporaryFeatureEnabled,
                                        hideAddLinkedProduct: !isAddLinkedProductEnabled,
                                        hideAddTemporaryWarranty: !isAddTemporaryWarrantyEnabled,
                                        hideFindSimilarProducts: !isFindSimilarEnabled,
                                        hideTotalPrice,
                                        // The caller's header actions are the only primary actions
                                        // when it supplies them, so the package's finalize button
                                        // is hidden to avoid a second one.
                                        hideOnFinalize: isView || Boolean(headerActions),
                                        endChildren: headerEndChildren,
                                    }}
                                    // Sized by the tenant, with the package defaults standing when
                                    // it configured none. The collapse controls the package renders
                                    // in the header are the on-demand companion to the widths.
                                    collapsibleSidePanels
                                    sidebarWidth={sidebarWidth}
                                    sidebarSubtitle={sidebarSubtitle}
                                    recapWidth={recapWidth}
                                    defaultGroupsExpanded={defaultGroupsExpanded}
                                    defaultRecapFeaturesExpanded={defaultRecapFeaturesExpanded}
                                    defaultRecapExpanded={defaultRecapExpanded}
                                    httpClientInstance={beEngineHttpClient}
                                    linkedProductsAssignments={[]}
                                />
                            </SidebarProvider>
                        </ConfiguratorThemeProvider>
                    )}
                {/* Overlay for the prototype prerequisite queries only. Once the Configurator is
                    mounted it owns the loading/backdrop for the apply-assignments load itself. */}
                {(loadingPrototypeNode ||
                    loadingPrototypeSchema ||
                    loadingModelNode ||
                    loadingConfiguratorDefaults) && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        bgcolor="background.default"
                        zIndex={100001}
                    >
                        <CircularProgress size="medium" />
                    </Box>
                )}
            </Box>

            <SaveTemplateDialog
                open={templateConfigJson !== undefined}
                configurationJson={templateConfigJson ?? ''}
                prototypeId={prototypeId}
                onClose={() => setTemplateConfigJson(undefined)}
            />

            <Snackbar
                open={viewSwitchResetNotice}
                autoHideDuration={4000}
                onClose={() => setViewSwitchResetNotice(false)}
                message={formatMessage({
                    id: 'cpq.configurator.view-switch-reset',
                    defaultMessage: 'Selections not valid in this view were reset',
                })}
            />

            <PricingTraceDrawer
                open={explainConfigJson !== undefined}
                configurationJson={explainConfigJson ?? ''}
                onClose={() => setExplainConfigJson(undefined)}
            />
        </Drawer>
    );
}
